import React from "react";
import "./SkeletonLoader.css";

interface SkeletonLoaderProps {
    variant?: "list" | "card" | "row" | "text";
    rows?: number;
    height?: number | string;
    width?: number | string;
    className?: string;
}

function SkeletonBlock({
    width = "100%",
    height = 18,
    style,
}: {
    width?: number | string;
    height?: number | string;
    style?: React.CSSProperties;
}) {
    return (
        <div
            className="sk-block"
            style={{ width, height, ...style }}
            aria-hidden="true"
        />
    );
}

export default function SkeletonLoader({
    variant = "list",
    rows = 4,
    height,
    width,
    className = "",
}: SkeletonLoaderProps) {
    if (variant === "card") {
        return (
            <div className={`sk-card ${className}`} aria-busy="true" aria-label="Loading...">
                <SkeletonBlock height={120} />
                <div className="sk-card-body">
                    <SkeletonBlock height={18} width="60%" />
                    <SkeletonBlock height={14} width="80%" style={{ marginTop: 8 }} />
                    <SkeletonBlock height={14} width="40%" style={{ marginTop: 6 }} />
                </div>
            </div>
        );
    }

    if (variant === "row") {
        return (
            <div
                className={`sk-row ${className}`}
                aria-busy="true"
                aria-label="Loading..."
                style={{ height, width }}
            >
                <SkeletonBlock height={height ?? 44} width={width ?? "100%"} />
            </div>
        );
    }

    if (variant === "text") {
        return (
            <div className={`sk-text ${className}`} aria-busy="true" aria-label="Loading...">
                <SkeletonBlock height={14} width="90%" />
                <SkeletonBlock height={14} width="75%" style={{ marginTop: 6 }} />
            </div>
        );
    }

    // Default: list
    return (
        <div className={`sk-list ${className}`} aria-busy="true" aria-label="Loading...">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="sk-list-item">
                    <SkeletonBlock height={40} width={40} style={{ borderRadius: "50%", flexShrink: 0 }} />
                    <div className="sk-list-lines">
                        <SkeletonBlock height={14} width={`${60 + (i % 3) * 10}%`} />
                        <SkeletonBlock height={12} width={`${40 + (i % 2) * 15}%`} style={{ marginTop: 6 }} />
                    </div>
                    <SkeletonBlock height={28} width={64} style={{ borderRadius: 6, flexShrink: 0 }} />
                </div>
            ))}
        </div>
    );
}
